/**
 * PrimaryButton Component
 *
 * UI/Aesthetics: Primary action button with shadcn styling, simplified color variants
 * Functions: Standard button functionality with configurable variants
 * Uses shadcn Button as the base with consistent design system styling
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PrimaryButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'variant' | 'size'> {
  /** Button variant - maps to shadcn variants for consistency */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';
  /** Legacy color prop for backward compatibility */
  color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
  children?: React.ReactNode;
  /** Icon to display at the start of the button (for MUI compatibility) */
  startIcon?: React.ReactNode;
  /** Size of the button - supports MUI 'small' for compatibility */
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'small';
}

export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  variant,
  color = 'primary',
  children,
  className,
  startIcon,
  size,
  ...props
}) => {
  // Map legacy color prop to shadcn variants for backward compatibility
  const getVariantFromColor = () => {
    if (variant) return variant; // Use variant prop if provided

    switch (color) {
      case 'error':
      case 'warning':
        return 'destructive';
      case 'secondary':
        return 'secondary';
      case 'primary':
      case 'info':
      case 'success':
      default:
        return 'default';
    }
  };

  // Convert MUI size to shadcn size
  const getButtonSize = (): 'default' | 'sm' | 'lg' | 'icon' => {
    return size === 'small'
      ? 'sm'
      : (size as 'default' | 'sm' | 'lg' | 'icon') || 'default';
  };

  return (
    <Button
      variant={getVariantFromColor()}
      size={getButtonSize()}
      className={cn(className)}
      {...props}
    >
      {startIcon}
      {children}
    </Button>
  );
};

export default PrimaryButton;
